import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, X, Search, Play, Pause, Save, Trash2 } from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

/**
 * ChannelNarratorModal — Configure per-channel narrator (voice + audio settings).
 * Follows the same glass-modal style as Extras/Anotações in CanalManagerNew.
 */
export default function ChannelNarratorModal({ isOpen, onClose, canalId, canalName, onNarratorChanged }) {
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [voiceId, setVoiceId] = useState('');
  const [voiceName, setVoiceName] = useState('');
  const [speed, setSpeed] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  const [subtitleEnabled, setSubtitleEnabled] = useState(true);
  const [subtitleWords, setSubtitleWords] = useState(5);
  const [subtitleCase, setSubtitleCase] = useState('normal');
  const [subtitleVeoWords, setSubtitleVeoWords] = useState(5);

  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Voice preview
  const [previewingId, setPreviewingId] = useState(null);
  const previewAudioRef = useRef(null);

  // ── Fetch narrator config + voices on open ──
  useEffect(() => {
    if (!isOpen || !canalId) return;
    setLoading(true);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Fetch narrator config
    axios.get(`${BACKEND_URL}/api/canais/${canalId}/narrator`, { headers })
      .then(res => {
        if (res.data.configured) {
          setConfigured(true);
          setVoiceId(res.data.voice_id || '');
          setVoiceName(res.data.voice_name || '');
          setSpeed(res.data.speed ?? 1.0);
          setVolume(res.data.volume ?? 1.0);
          setSubtitleEnabled(res.data.subtitle_enabled ?? true);
          setSubtitleWords(res.data.subtitle_words ?? 5);
          setSubtitleCase(res.data.subtitle_case || 'normal');
          setSubtitleVeoWords(res.data.subtitle_veo_words ?? 5);
        } else {
          setConfigured(false);
          resetForm();
        }
      })
      .catch(() => { resetForm(); })
      .finally(() => setLoading(false));

    // Fetch voices (same API as dashboard)
    setLoadingVoices(true);
    axios.get(`${BACKEND_URL}/api/audio-tts/voices`)
      .then(res => setVoices(res.data?.voices || []))
      .catch(() => setVoices([]))
      .finally(() => setLoadingVoices(false));
  }, [isOpen, canalId]);

  const resetForm = () => {
    setVoiceId('');
    setVoiceName('');
    setSpeed(1.0);
    setVolume(1.0);
    setSubtitleEnabled(true);
    setSubtitleWords(5);
    setSubtitleCase('normal');
    setSubtitleVeoWords(5);
  };

  // ── Save narrator ──
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/canais/${canalId}/narrator`, {
        voice_id: voiceId || null,
        voice_name: voiceName || null,
        speed, volume,
        subtitle_enabled: subtitleEnabled,
        subtitle_words: subtitleWords,
        subtitle_case: subtitleCase,
        subtitle_veo_words: subtitleVeoWords,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Narrador configurado para '${canalName}'`);
      onNarratorChanged?.();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao salvar narrador';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Remove narrator ──
  const handleRemove = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/canais/${canalId}/narrator`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Narrador removido');
      onNarratorChanged?.();
      onClose();
    } catch (err) {
      toast.error('Erro ao remover narrador');
    } finally {
      setDeleting(false);
    }
  };

  // ── Voice preview ──
  const togglePreview = (voice) => {
    if (previewingId === voice.id) {
      previewAudioRef.current?.pause();
      setPreviewingId(null);
      return;
    }
    if (voice.preview_url) {
      if (previewAudioRef.current) previewAudioRef.current.pause();
      const audio = new Audio(voice.preview_url);
      previewAudioRef.current = audio;
      audio.play().catch(() => {});
      audio.onended = () => setPreviewingId(null);
      setPreviewingId(voice.id);
    }
  };

  // Filter voices
  const filteredVoices = voices.filter(v =>
    !searchTerm || (v.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (v.language || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        animation: 'dp-modal-overlay 0.2s ease',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--dp-bg-glass)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid var(--dp-border-medium)', borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)', padding: '0',
          maxWidth: '480px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: 'dp-modal-scale 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <div style={{ padding: '20px 24px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '12px',
              background: 'rgba(13, 174, 255, 0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Mic size={18} style={{ color: 'var(--dp-blue-500)' }} />
            </div>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', margin: 0 }}>
                Configurar Narrador
              </h3>
              <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', margin: '2px 0 0 0' }}>
                {canalName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid var(--dp-border-subtle)',
              borderRadius: '10px', width: '32px', height: '32px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--dp-text-tertiary)', transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* ─── Body (scrollable) ─── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--dp-text-tertiary)', fontSize: '13px' }}>
              Carregando...
            </div>
          ) : (
            <>
              {/* ─── VOZ Section ─── */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Voz
                </div>

                {/* Search */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '8px', padding: '6px 10px', marginBottom: '8px',
                }}>
                  <Search size={13} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
                  <input
                    type="text"
                    placeholder="Buscar voz..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      background: 'transparent', border: 'none', outline: 'none', width: '100%',
                      fontSize: '12px', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                    }}
                  />
                </div>

                {/* Voice list */}
                <div style={{
                  maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '8px',
                }}>
                  {loadingVoices ? (
                    <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
                      Carregando vozes...
                    </div>
                  ) : filteredVoices.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
                      Nenhuma voz encontrada
                    </div>
                  ) : filteredVoices.map(voice => (
                    <div
                      key={voice.id}
                      onClick={() => { setVoiceId(voice.id); setVoiceName(voice.name); }}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', cursor: 'pointer',
                        background: voiceId === voice.id ? 'rgba(13, 174, 255, 0.08)' : 'transparent',
                        borderBottom: '1px solid var(--dp-border-subtle)',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { if (voiceId !== voice.id) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                      onMouseLeave={(e) => { if (voiceId !== voice.id) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {voiceId === voice.id && (
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--dp-blue-500)', flexShrink: 0 }} />
                        )}
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: voiceId === voice.id ? 600 : 400, color: voiceId === voice.id ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)' }}>
                            {voice.name}
                          </div>
                          {voice.language && (
                            <div style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', marginTop: '1px' }}>
                              {voice.language}
                            </div>
                          )}
                        </div>
                      </div>
                      {voice.preview_url && (
                        <button
                          onClick={(e) => { e.stopPropagation(); togglePreview(voice); }}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: previewingId === voice.id ? 'var(--dp-blue-500)' : 'var(--dp-text-tertiary)',
                            padding: '4px', display: 'flex', alignItems: 'center',
                          }}
                        >
                          {previewingId === voice.id ? <Pause size={13} /> : <Play size={13} />}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {voiceId && (
                  <div style={{ fontSize: '11px', color: 'var(--dp-blue-500)', marginTop: '6px', fontWeight: 500 }}>
                    Selecionada: {voiceName || voiceId}
                  </div>
                )}
              </div>

              {/* ─── ÁUDIO Section ─── */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                  Áudio
                </div>

                {/* Speed slider */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>Velocidade</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>{speed.toFixed(1)}x</span>
                  </div>
                  <input type="range" min="0.5" max="1.5" step="0.1" value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))} className="ag-slider" style={{ width: '100%' }} />
                </div>

                {/* Volume slider */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>Volume</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>{volume.toFixed(1)}x</span>
                  </div>
                  <input type="range" min="0.5" max="2" step="0.1" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} className="ag-slider" style={{ width: '100%' }} />
                </div>
              </div>

              {/* Separator */}
              <div style={{ height: '1px', background: 'var(--dp-border-subtle)', margin: '0 0 20px 0' }} />

              {/* ─── LEGENDA Section ─── */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                  Legenda
                </div>

                {/* SRT Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>Gerar Legenda</span>
                  <button
                    onClick={() => setSubtitleEnabled(!subtitleEnabled)}
                    style={{
                      width: '34px', height: '19px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                      position: 'relative', flexShrink: 0,
                      background: subtitleEnabled ? 'var(--dp-blue-500)' : 'var(--dp-border-medium)',
                      transition: 'background 0.2s',
                    }}
                  >
                    <div style={{
                      position: 'absolute', width: '15px', height: '15px', borderRadius: '50%',
                      background: 'white', top: '2px',
                      right: subtitleEnabled ? '2px' : 'auto',
                      left: subtitleEnabled ? 'auto' : '2px',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </button>
                </div>

                {subtitleEnabled && (
                  <>
                    {/* Legenda (legenda.srt): máximo de palavras por linha, com pausas naturais */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>Legenda (máx. palavras)</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>máx. {subtitleWords}</span>
                      </div>
                      <input type="range" min="1" max="15" step="1" value={subtitleWords} onChange={(e) => setSubtitleWords(parseInt(e.target.value))} className="ag-slider" style={{ width: '100%' }} />
                    </div>

                    {/* Format buttons */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', marginBottom: '6px' }}>Formato</div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {[
                          { id: 'normal', label: 'Aa' },
                          { id: 'upper', label: 'AA' },
                          { id: 'lower', label: 'aa' },
                        ].map(opt => (
                          <button
                            key={opt.id}
                            onClick={() => setSubtitleCase(opt.id)}
                            style={{
                              flex: 1, padding: '6px 0', textAlign: 'center', borderRadius: '9999px',
                              border: subtitleCase === opt.id ? '1px solid var(--dp-blue-500)' : '1px solid var(--dp-border-subtle)',
                              background: subtitleCase === opt.id ? 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))' : 'transparent',
                              color: subtitleCase === opt.id ? 'white' : 'var(--dp-text-secondary)',
                              fontSize: '12px', fontWeight: 600, fontFamily: 'var(--dp-font)', cursor: 'pointer',
                              transition: 'all 0.3s',
                              boxShadow: subtitleCase === opt.id ? '0 0 12px rgba(13,174,255,0.25)' : 'none',
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Por palavra (srtsyncpalavra.srt): total FIXO de palavras por linha */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>Por palavra (total fixo)</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>{subtitleVeoWords}</span>
                      </div>
                      <input type="range" min="1" max="15" step="1" value={subtitleVeoWords} onChange={(e) => setSubtitleVeoWords(parseInt(e.target.value))} className="ag-slider" style={{ width: '100%' }} />
                      <div style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', lineHeight: '1.3', marginTop: '4px' }}>
                        Número fixo de palavras em cada linha: os blocos ficam com duração parecida. Ideal para prompts e vídeos com tempos semelhantes.
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* ─── Footer ─── */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid var(--dp-border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {configured ? (
            <button
              onClick={handleRemove}
              disabled={deleting}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                background: 'none', border: '1px solid rgba(239, 68, 68, 0.30)',
                borderRadius: '8px', padding: '6px 12px', cursor: deleting ? 'not-allowed' : 'pointer',
                fontSize: '12px', fontWeight: 500, color: 'var(--dp-red-500, #F87171)',
                fontFamily: 'var(--dp-font)', transition: 'all 0.2s',
                opacity: deleting ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { if (!deleting) e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              <Trash2 size={12} />
              {deleting ? 'Removendo...' : 'Remover'}
            </button>
          ) : <div />}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid var(--dp-border-subtle)',
                borderRadius: '8px', padding: '6px 16px', cursor: 'pointer',
                fontSize: '12px', fontWeight: 500, color: 'var(--dp-text-secondary)',
                fontFamily: 'var(--dp-font)', transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                background: saving ? 'var(--dp-border-medium)' : 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))',
                border: 'none', borderRadius: '8px', padding: '6px 16px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '12px', fontWeight: 600, color: 'white',
                fontFamily: 'var(--dp-font)', transition: 'all 0.3s',
                boxShadow: saving ? 'none' : '0 0 16px rgba(13,174,255,0.2)',
                opacity: saving ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { if (!saving) e.currentTarget.style.boxShadow = '0 0 24px rgba(13,174,255,0.35)'; }}
              onMouseLeave={(e) => { if (!saving) e.currentTarget.style.boxShadow = '0 0 16px rgba(13,174,255,0.2)'; }}
            >
              <Save size={12} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
